import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack5';
import { useTheme, useMediaQuery } from '@material-ui/core';

import { Icon } from '@iconify/react';
// material
import {
  Card,
  Stack,
  Container,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  Alert,
  Tooltip,
  TextField,
  Pagination
} from '@material-ui/core';
import {
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon
} from '@material-ui/icons';

// components
import Page from '../components/Page';
import LoadingScreen from '../components/LoadingScreen';

// API service
import employeeDocumentsApi from '../utils/api/employeeDocuments';

const EmployeeRequiredDocuments = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [currentDocument, setCurrentDocument] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [isRetrying, setIsRetrying] = useState(false);
  const [expirationDates, setExpirationDates] = useState({});
  const [expirationDateErrors, setExpirationDateErrors] = useState({});
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  
  const fileInputRef = useRef({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize file input refs for each document
  useEffect(() => {
    fileInputRef.current = requiredDocuments.reduce((acc, doc) => {
      acc[doc.id] = React.createRef();
      return acc;
    }, {});
  }, [requiredDocuments]);

  // Load required documents and previously uploaded documents
  const loadData = async () => {
    setLoading(true);
    setLoadError(null);
    setIsRetrying(false);
    
    try {
      // Load required documents
      const requiredDocsResponse = await employeeDocumentsApi.getRequiredDocuments();
      if (requiredDocsResponse.ok && Array.isArray(requiredDocsResponse.data)) {
        setRequiredDocuments(requiredDocsResponse.data);
      } else {
        console.error('My Documents data issue:', requiredDocsResponse);
        setRequiredDocuments([]);
        setLoadError(requiredDocsResponse.error || 'Error loading document requirements');
        enqueueSnackbar(requiredDocsResponse.error || 'Error loading document requirements', { variant: 'error' });
      }
      
      // Load uploaded documents
      const uploadedDocsResponse = await employeeDocumentsApi.getUploadedDocuments();
      if (uploadedDocsResponse.ok) {
        setUploadedDocuments(uploadedDocsResponse.data);
      } else {
        console.error('Uploaded documents data issue:', uploadedDocsResponse);
        setUploadedDocuments({});
        enqueueSnackbar(uploadedDocsResponse.error || 'Error loading your uploaded documents', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error in loadData:', error);
      setLoadError(error.message || 'Failed to load documents');
      enqueueSnackbar(error.message || 'Failed to load documents', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [enqueueSnackbar]);

  // Reset to first page when documents change
  useEffect(() => {
    setCurrentPage(1);
  }, [requiredDocuments.length]);

  // In the component, add a useEffect to log documents after they're loaded
  useEffect(() => {
    if (requiredDocuments.length > 0) {
      // Check which documents require expiration
      requiredDocuments.forEach(doc => {
        console.log(`Document ${doc.name} requires expiration:`, doc.requiresExpiration);
      });
    }
  }, [requiredDocuments]);

  // Handle file selection
  const handleFileSelect = (docId) => (event) => {
    const newFiles = Array.from(event.target.files);
    const doc = requiredDocuments.find(d => getDocumentId(d) === docId);
    
    if (!newFiles || newFiles.length === 0) return;
    
    // Determine updatedFiles: add more or replace for multi-file docs
    let updatedFiles;
    if (doc.numberOfFiles > 1) {
      const existingFiles = selectedFiles[docId] || [];
      if (existingFiles.length >= doc.numberOfFiles) {
        // Replace scenario: reset with new selection up to the limit
        updatedFiles = newFiles.slice(0, doc.numberOfFiles);
      } else {
        // Add more scenario: combine existing and new
        updatedFiles = [...existingFiles, ...newFiles].slice(0, doc.numberOfFiles);
      }
    } else {
      // For single file requirements, just use the latest selection
      updatedFiles = newFiles;
    }
    
    // Validate the updated file collection
    const errors = employeeDocumentsApi.validateFiles(updatedFiles, doc);
    
    setSelectedFiles(prev => ({
      ...prev,
      [docId]: updatedFiles
    }));
    
    setFileErrors(prev => ({
      ...prev,
      [docId]: errors
    }));
    
    // Clear the input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle expiration date change
  const handleExpirationDateChange = (docId, value) => {
    // Format: YYYY-MM-DD
    setExpirationDates(prev => ({
      ...prev,
      [docId]: value
    }));
    
    // Validate date
    let error = '';
    if (!value) {
      error = 'Expiration date is required';
    } else {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        error = 'Expiration date cannot be in the past';
      }
    }
    
    setExpirationDateErrors(prev => ({
      ...prev,
      [docId]: error
    }));
  };

  // Handle file upload
  const handleUpload = async (docId) => {
    console.log('handleUpload', docId);
    const files = selectedFiles[docId];
    if (!files || files.length === 0) {
      return enqueueSnackbar('No files selected', { variant: 'warning' });
    }
    
    // Check for validation errors
    if (fileErrors[docId] && fileErrors[docId].length > 0) {
      return enqueueSnackbar('Please fix file errors before uploading', { variant: 'error' });
    }
    
    // Check if document requires expiration date
    const doc = requiredDocuments.find(d => getDocumentId(d) === docId);
    if (doc && doc.requiresExpiration) {
      if (!expirationDates[docId]) {
        setExpirationDateErrors(prev => ({
          ...prev,
          [docId]: 'Expiration date is required'
        }));
        return enqueueSnackbar('Please enter an expiration date', { variant: 'error' });
      }
      
      if (expirationDateErrors[docId]) {
        return enqueueSnackbar('Please fix expiration date error before uploading', { variant: 'error' });
      }
    }
    
    setCurrentDocument(docId);
    
    // Set initial progress
    setUploadProgress(prev => ({
      ...prev,
      [docId]: 0
    }));
    
    // Simulate gradual progress
    const simulateProgress = () => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.floor(Math.random() * 20) + 5;
        if (progress > 95) progress = 95; // Cap at 95% until actual upload completes
        
        setUploadProgress(prev => ({
          ...prev,
          [docId]: progress
        }));
        
        if (progress === 95) {
          clearInterval(interval);
        }
      }, 300);
      
      return interval;
    };
    
    const progressInterval = simulateProgress();
    
    try {
      // Upload document using API with expiration date if required
      const uploadData = {
        files,
        expirationDate: doc && doc.requiresExpiration ? new Date(expirationDates[docId]) : null
      };
      
      const uploadResponse = await employeeDocumentsApi.uploadDocument(docId, uploadData);
      
      clearInterval(progressInterval);
      setUploadProgress(prev => ({
        ...prev,
        [docId]: 100
      }));
      
      if (uploadResponse.ok) {
        // Refresh uploaded documents
        const uploadedDocsResponse = await employeeDocumentsApi.getUploadedDocuments();
        if (uploadedDocsResponse.ok) {
          setUploadedDocuments(uploadedDocsResponse.data);
        }
        
        // Clear selected files and expiration date for this document
        setSelectedFiles(prev => {
          const updated = { ...prev };
          delete updated[docId];
          return updated;
        });
        
        setExpirationDates(prev => {
          const updated = { ...prev };
          delete updated[docId];
          return updated;
        });
        
        setExpirationDateErrors(prev => {
          const updated = { ...prev };
          delete updated[docId];
          return updated;
        });
        
        enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(uploadResponse.error || 'Error uploading document', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      enqueueSnackbar('Error uploading document', { variant: 'error' });
      clearInterval(progressInterval);
    } finally {
      setCurrentDocument(null);
      setTimeout(() => {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[docId];
          return updated;
        });
      }, 1000);
    }
  };

  // Delete an uploaded document
  const handleDelete = async (docId) => {
    const uploadedDoc = uploadedDocuments[docId];
    const isApproved = uploadedDoc && uploadedDoc.status === 'approved';
    
    const confirmMessage = isApproved 
      ? 'Are you sure you want to delete this approved document? You will need to upload it again for approval.'
      : 'Are you sure you want to delete this document?';
      
    if (window.confirm(confirmMessage)) {
      try {
        const response = await employeeDocumentsApi.deleteDocument(docId);
        
        if (response.ok) {
          // Update state by removing the deleted document
          setUploadedDocuments(prev => {
            const updated = { ...prev };
            delete updated[docId];
            return updated;
          });
          
          enqueueSnackbar('Document deleted successfully', { variant: 'success' });
        } else {
          enqueueSnackbar(response.error || 'Error deleting document', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error deleting document:', error);
        enqueueSnackbar('Error deleting document', { variant: 'error' });
      }
    }
  };

  // Open file preview dialog
  const handlePreview = (docId, fileIndex) => {
    const uploadedDoc = uploadedDocuments[docId];
    if (uploadedDoc && uploadedDoc.files[fileIndex]) {
      setPreviewFile(uploadedDoc.files[fileIndex]);
      setOpenPreviewDialog(true);
    }
  };

  // Determine document status text and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'approved':
        return { label: 'Approved', color: 'success', icon: <CheckCircleIcon /> };
      case 'rejected':
        return { label: 'Rejected', color: 'error', icon: <ErrorIcon /> };
      case 'pending_approval':
        return { label: 'Pending Approval', color: 'warning', icon: <WarningIcon /> };
      case 'expired':
        return { label: 'Expired', color: 'error', icon: <ErrorIcon /> };
      default:
        return { label: 'Not Uploaded', color: 'default', icon: null };
    }
  };

  // Handle browse files button click
  const handleBrowseClick = (docId) => {
    if (fileInputRef.current[docId]) {
      fileInputRef.current[docId].current.click();
    }
  };

  // Handle refresh button click
  const handleRefresh = () => {
    setIsRetrying(true);
    loadData();
  };

  // Get document ID from MongoDB _id or regular id
  const getDocumentId = (doc) => doc._id || doc.id;
  
  // Clear selected files for a document
  const handleClearFiles = (docId) => {
    setSelectedFiles(prev => {
      const updated = { ...prev };
      delete updated[docId];
      return updated;
    });
    
    setFileErrors(prev => {
      const updated = { ...prev };
      delete updated[docId];
      return updated;
    });
  };

  // Remove a single selected file for a document
  const handleRemoveSelectedFile = (docId, index) => {
    setSelectedFiles(prev => {
      const updatedFiles = [...(prev[docId] || [])];
      updatedFiles.splice(index, 1);
      return { ...prev, [docId]: updatedFiles };
    });
    // Re-validate files for this document
    const doc = requiredDocuments.find(d => getDocumentId(d) === docId);
    const updated = (selectedFiles[docId] || []).filter((_, i) => i !== index);
    const errors = employeeDocumentsApi.validateFiles(updated, doc);
    setFileErrors(prev => ({ ...prev, [docId]: errors }));
  };

  // Preview a single selected file
  const handlePreviewSelected = (docId, index) => {
    const file = selectedFiles[docId][index];
    const url = URL.createObjectURL(file);
    setPreviewFile({ name: file.name, type: file.type, dataUrl: url });
    setOpenPreviewDialog(true);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date provided';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top of documents section when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination values
  const totalPages = Math.ceil(requiredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDocuments = requiredDocuments.slice(startIndex, endIndex);

  return (
    <Page title="My Documents | Time In">
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            My Documents
          </Typography>
          <Button
            variant="outlined"
            startIcon={isRetrying ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading || isRetrying}
          >
            {isRetrying ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
        
        {isLoading ? (
          <Box sx={{ height: '50vh' }}>
            <LoadingScreen />
          </Box>
        ) : (
          <>
            {loadError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {loadError}. <Button onClick={handleRefresh}>Try Again</Button>
              </Alert>
            )}
            
            {requiredDocuments.length === 0 ? (
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">
                  No document requirements found for your company.
                </Typography>
              </Card>
            ) : (
              <>
                <Grid container spacing={3}>
                  {currentDocuments.map(doc => {
                  const docId = getDocumentId(doc);
                  const uploadedDoc = uploadedDocuments[docId];
                  const isUploaded = !!uploadedDoc;
                  const isUploading = currentDocument === docId;
                  const progress = uploadProgress[docId] || 0;
                  const selectedFileCount = selectedFiles[docId]?.length || 0;
                  const hasFileErrors = fileErrors[docId] && fileErrors[docId].length > 0;
                  const statusInfo = isUploaded 
                    ? getStatusInfo(uploadedDoc.status) 
                    : getStatusInfo('not_uploaded');
                  // Log requiresExpiration status for this document
                  console.log(`Rendering document ${doc.name} with requiresExpiration:`, doc.requiresExpiration);
                  // Ensure requiresExpiration is a boolean
                  const requiresExpiration = !!doc.requiresExpiration;
                  const expirationDateError = expirationDateErrors[docId];
                  
                  return (
                    <Grid item xs={12} key={docId}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 3,
                          position: 'relative',
                          borderLeft: isUploaded ? `6px solid ${statusInfo.color === 'success' ? '#4caf50' : statusInfo.color === 'warning' ? '#ff9800' : statusInfo.color === 'error' ? '#f44336' : '#e0e0e0'}` : 'none'
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={7}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6" gutterBottom>
                                  {doc.name}
                                </Typography>
                                {doc.isRequired && (
                                  <Chip label="Required" size="small" color="primary" />
                                )}
                                {doc.description && (
                                  <Tooltip title={doc.description}>
                                    <InfoIcon fontSize="small" color="action" />
                                  </Tooltip>
                                )}
                              </Box>
                              {isMobile && (
                                <Tooltip
                                  enterTouchDelay={0}
                                  title={
                                    <Box>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <b>Accepted formats:</b> {doc.allowedFileTypes.map(type => type.toUpperCase()).join(', ')}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <b>Maximum file size:</b> {doc.maxFileSize} MB
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <b>Required files:</b> {doc.numberOfFiles === 1 ? '1 file' : `${doc.numberOfFiles} files`}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow={true}
                                >
                                  <IconButton size="small">
                                    <Icon icon="material-symbols:info-outline" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                            {/* Desktop: show info inline, Mobile: show in tooltip only */}
                            {!isMobile && (
                              <>
                                <Stack direction="row" spacing={1} mb={1}>
                                  <Typography variant="body2" color="text.secondary">
                                    Accepted formats:
                                  </Typography>
                                  {doc.allowedFileTypes.map(type => (
                                    <Chip
                                      key={type}
                                      label={type.toUpperCase()}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Maximum file size: <b>{doc.maxFileSize} MB</b>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {doc.numberOfFiles === 1 
                                    ? "Required: 1 file" 
                                    : `Required: ${doc.numberOfFiles} files`}
                                </Typography>
                              </>
                            )}
                            
                            {isUploaded && (
                              <Box mt={2}>
                                <Chip
                                  icon={statusInfo.icon}
                                  label={statusInfo.label}
                                  color={statusInfo.color}
                                  size="small"
                                />
                                
                                {uploadedDoc.expirationDate && (
                                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Expires on: <b>{formatDate(uploadedDoc.expirationDate)}</b>
                                  </Typography>
                                )}
                                
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                  Uploaded on: {new Date(uploadedDoc.uploadedAt).toLocaleString()}
                                </Typography>
                                {uploadedDoc.rejectionReason && (
                                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                    Reason: {uploadedDoc.rejectionReason}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Grid>
                          
                          <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              {isUploaded ? (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Uploaded Files:
                                  </Typography>
                                  <List dense>
                                    {uploadedDoc.files.map((file, index) => (
                                      <ListItem
                                        key={index}
                                        secondaryAction={
                                          <Button 
                                            color="primary" variant="outlined" size="small" onClick={() => handlePreview(docId, index)} sx={{ ml: 1 }}>
                                            View
                                          </Button>
                                        }
                                      >
                                        <ListItemText
                                          primary={file.name}
                                          secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                          primaryTypographyProps={{ sx: { wordBreak: 'break-word', whiteSpace: 'normal' } }}
                                          sx={{ pr: 8 }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                  
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDelete(docId)}
                                    size="small"
                                    sx={{ mt: 1 }}
                                  >
                                    Remove All
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                  <input
                                    type="file"
                                    multiple={doc.numberOfFiles > 1}
                                    accept={doc.allowedFileTypes.map(type => `.${type}`).join(',')}
                                    onChange={handleFileSelect(docId)}
                                    style={{ display: 'none' }}
                                    ref={fileInputRef.current[docId]}
                                  />
                                  
                                  {doc.numberOfFiles > 1 && selectedFileCount === 0 && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                      You can select multiple files at once, or add them one by one.
                                    </Alert>
                                  )}
                                  
                                  {/* Log if we're going to show the expiration date input */}
                                  {console.log(`Should show expiration input for ${doc.name}:`, requiresExpiration)}
                                  
                                  {requiresExpiration && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        This document requires an expiration date:
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        label="Document Expiration Date"
                                        type="date"
                                        value={expirationDates[docId] || ''}
                                        onChange={(e) => handleExpirationDateChange(docId, e.target.value)}
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        InputProps={{
                                          startAdornment: <CalendarIcon color="action" sx={{ mr: 1 }} />,
                                        }}
                                        error={!!expirationDateError}
                                        helperText={expirationDateError || 'Enter the expiration date shown on the document'}
                                        disabled={isUploading}
                                        sx={{ mb: 1 }}
                                      />
                                    </Box>
                                  )}
                                  
                                  {selectedFileCount > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      {selectedFiles[docId].map((file, index) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Box>
                                              <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                {file.name}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {(file.size / 1024 / 1024).toFixed(2)} MB
                                              </Typography>
                                            </Box>
                                            <Button
                                              color="error"
                                              variant="outlined"
                                              startIcon={<DeleteIcon />}
                                              size="small"
                                              onClick={() => handleRemoveSelectedFile(docId, index)}
                                              disabled={isUploading}
                                            >
                                              Remove
                                            </Button>
                                          </Stack>
                                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                            <Button
                                              color="primary"
                                              variant="outlined"
                                              size="small"
                                              onClick={() => handlePreviewSelected(docId, index)}
                                            >
                                              View
                                            </Button>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  
                                  {hasFileErrors && (
                                    <Box sx={{ mb: 2 }}>
                                      <FormHelperText error>
                                        {fileErrors[docId].map((error, index) => (
                                          <Typography key={index} variant="caption" component="div" color="error">
                                            • {error}
                                          </Typography>
                                        ))}
                                      </FormHelperText>
                                    </Box>
                                  )}
                                  
                                  <Stack direction="row" spacing={2}>
                                    <Button
                                      variant="outlined"
                                      startIcon={<AttachFileIcon />}
                                      onClick={() => handleBrowseClick(docId)}
                                      disabled={isUploading}
                                    >
                                      {selectedFileCount === 0 
                                        ? `Select ${doc.numberOfFiles > 1 ? 'Files' : 'File'}`
                                        : selectedFileCount < doc.numberOfFiles 
                                          ? `Add More (${doc.numberOfFiles - selectedFileCount} needed)`
                                          : 'Replace Files'}
                                    </Button>
                                    
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                      onClick={() => handleUpload(docId)}
                                      disabled={
                                        isUploading || 
                                        !selectedFiles[docId] || 
                                        hasFileErrors || 
                                        selectedFileCount !== doc.numberOfFiles ||
                                        (requiresExpiration && (!expirationDates[docId] || !!expirationDateError))
                                      }
                                    >
                                      Upload
                                    </Button>
                                  </Stack>
                                  
                                  {isUploading && (
                                    <Box sx={{ width: '100%', mt: 2 }}>
                                      <LinearProgress variant="determinate" value={progress} />
                                      <Typography variant="caption" align="center" display="block">
                                        {progress}% Uploaded
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                  })}
                </Grid>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      mt: 4,
                      mb: 2
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Showing {startIndex + 1}-{Math.min(endIndex, requiredDocuments.length)} of {requiredDocuments.length} documents
                      </Typography>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                      />
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </>
        )}
        
        {/* File Preview Dialog */}
        <Dialog
          open={openPreviewDialog}
          onClose={() => setOpenPreviewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            File Preview
            <IconButton
              aria-label="close"
              onClick={() => setOpenPreviewDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewFile && (
              <Box sx={{ textAlign: 'center' }}>
                {previewFile.type.includes('image') ? (
                  <img
                    src={previewFile.dataUrl}
                    alt={previewFile.name}
                    style={{ maxWidth: '100%', maxHeight: '70vh' }}
                  />
                ) : previewFile.type.includes('pdf') ? (
                  <iframe
                    src={previewFile.dataUrl}
                    title={previewFile.name}
                    width="100%"
                    height="500px"
                  />
                ) : (
                  <Typography variant="body1">
                    Preview not available for this file type. Click download to view the file.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {previewFile && (
              <Button
                variant="outlined"
                href={previewFile.dataUrl}
                download={previewFile.name}
              >
                Download
              </Button>
            )}
            <Button onClick={() => setOpenPreviewDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default EmployeeRequiredDocuments;
